import produce from 'immer';
import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import { resources } from '../../commons/constants/languages';

import { RootModel } from '../models';
import {
  DEFAULT_PAGINATION,
  LANGUAGE_HASH_PARAM,
  PROPERTIES_SHARE_RESULT_PAGE_LINK,
} from '../../commons/constants';
import { storeName as projectChildItemEntityStore } from '../entities/projectChildItem.entity';
import {
  PageRequest,
  PaginationResponseTypes,
  PaginationTypes,
  PropertiesResponseTypes,
} from '../../types/response';

export const storeName = 'propertiesModel';

export interface PropertiesCriteria {
  keyword?: string;
  statuses?: string[];
}

type PropertiesModelTypes = {
  codes: string[];
  loading: boolean;
  pagination: PaginationTypes;
  criteria: PropertiesCriteria;
  // share result page
  shareResultCodes: string[];
  shareResultLoading: boolean;
  shareResultPagination: PaginationTypes;
  shareResultCriteria: PropertiesCriteria;
  viewDetailCode: string;
};

const initialState: PropertiesModelTypes = {
  codes: null,
  loading: false,
  pagination: DEFAULT_PAGINATION,
  criteria: { keyword: '', statuses: [] },
  // share result page
  shareResultCodes: null,
  shareResultLoading: false,
  shareResultPagination: DEFAULT_PAGINATION,
  shareResultCriteria: { keyword: '', statuses: [] },
  viewDetailCode: null,
};

export const propertiesModel: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setCodes: produce((state, payload: string[]) => {
      state.codes = payload;
    }),
    setLoading: produce((state, payload: boolean) => {
      state.loading = payload;
    }),
    setPagination: produce((state, payload: PaginationTypes) => {
      state.pagination = payload;
    }),
    setCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    // share result page
    setShareResultCodes: produce((state, payload: string[]) => {
      state.shareResultCodes = payload;
    }),
    setShareResultLoading: produce((state, payload: boolean) => {
      state.shareResultLoading = payload;
    }),
    setShareResultPagination: produce((state, payload: PaginationTypes) => {
      state.shareResultPagination = payload;
    }),
    setShareResultCriteria: produce((state, payload) => {
      state.shareResultCriteria = payload;
    }),
    setViewDetailCode: produce((state, payload) => {
      state.viewDetailCode = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<any>) => ({
    async fetch(payload = {}, state) {
      this.setLoading(true);

      const { pagination = state[storeName].pagination, keyword, statuses } = payload;
      const resp: PaginationResponseTypes<PropertiesResponseTypes[]> = await dispatch[
        projectChildItemEntityStore
      ].fetchInventory({ ...pagination, ...payload });
      if (!resp) return;

      const { data } = resp;
      if (data && !!data.content) {
        this.setCodes(data.content.map((i) => i.code));
      }

      this.setPagination((({ content, ...otherProps }) => otherProps)(data));
      this.setCriteria({ keyword, statuses });

      this.setLoading(false);
    },
    async pagingAsync(payload: PageRequest, state) {
      if (state[storeName].loading) return;

      this.setLoading(true);
      const { criteria = {}, pagination = {} } = state[storeName];
      await this.fetch({ ...criteria, ...pagination, ...payload });
    },
    async getShareFilterResult(payload) {
      const result = await dispatch[projectChildItemEntityStore].shareFilterResult(payload);
      if (result) {
        const foundLang = Object.values(resources).find((lang) => lang.apiValue === payload.lang);

        window.open(
          `${PROPERTIES_SHARE_RESULT_PAGE_LINK.replace(':shareCode', result)}${
            foundLang ? `#${LANGUAGE_HASH_PARAM}=${foundLang.key}` : ''
          }`
        );
      }
    },
    async fetchByShareCode(payload = {}, state) {
      this.setShareResultLoading(true);

      const { pagination = state[storeName].shareResultPagination, keyword, statuses } = payload;
      const resp: PaginationResponseTypes<PropertiesResponseTypes[]> = await dispatch[
        projectChildItemEntityStore
      ].fetchInventoryBySharedCode({ ...pagination, ...payload });
      if (!resp) return null;

      const { data } = resp;
      if (data && !!data.content) {
        this.setShareResultCodes(data.content.map((i) => i.code));
      }

      this.setShareResultPagination((({ content, ...otherProps }) => otherProps)(data));
      this.setShareResultCriteria({ keyword, statuses });

      this.setShareResultLoading(false);
    },
    async shareResultPagingAsync(payload: PageRequest, state) {
      if (state[storeName].shareResultLoading) return;

      this.setShareResultLoading(true);
      const { shareResultCriteria = {}, shareResultPagination = {} } = state[storeName];
      await this.fetchByShareCode({ ...shareResultCriteria, ...shareResultPagination, ...payload });
    },
    async updateStatus(payload) {
      this.setLoading(true);
      try {
        await dispatch[projectChildItemEntityStore].updateStatus(payload);
        this.setLoading(false);
        message.success(`Success to update`);
      } catch (e) {
        console.error(e);
        message.error(`Failed to update`);
      } finally {
        this.setLoading(false);
      }
    },
    async updatePrice(payload) {
      this.setLoading(true);
      try {
        await dispatch[projectChildItemEntityStore].updatePrice(payload);
        this.setLoading(false);
        message.success(`Success to update`);
      } catch (e) {
        console.error(e);
        message.error(`Failed to update`);
      } finally {
        this.setLoading(false);
      }
    },
  }),
});

export default propertiesModel;
